import React, { useState, useEffect } from "react";
import CheckBox from "../components/CheckBox";
import { useSelector, useDispatch } from "react-redux";
import {
  getMappings,
  getOutputColumnsData,
  getInputColumnsData,
  getFileSettings,
  getOutputFileSettings,
  getEditStateData,
  getEditFileData,
  setProjectData,
  getFileDescription,
} from "../store/workspaceSlice";
import axiosAPI from "../config/axiosInstance";
import toast from "react-hot-toast";
import InputField from "./InputField";
import { getAllProjects } from "../api/mappingServices";
import { miniLoadingCricle } from "../assets/icons";
const SaveRules = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [projects, setprojects] = useState([]);
  const currentMaps = useSelector(getMappings);
  const outputColsData = useSelector(getOutputColumnsData);
  const inputColsData = useSelector(getInputColumnsData);
  const fileSettings = useSelector(getFileSettings);
  const outputFileSettings = useSelector(getOutputFileSettings);
  const editStateData = useSelector(getEditStateData);
  const description = useSelector(getFileDescription);
  const editFileData = useSelector(getEditFileData);
  const [fileName, setFileName] = useState(editFileData?.fileName || "");
  const [selectedProject, setSelectedProject] = useState(
    editFileData?.projectID || "",
  );

  const [saveAsNew, setsaveAsNew] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    getProjects();
  }, []);

  const getProjects = async () => {
    try {
      setIsLoading("fetchingProjects");
      const response = await getAllProjects();
      setprojects(response.data);
    } catch (error) {
      toast.error("Failed to fetch mappings");
    } finally {
      setIsLoading(null);
    }
  };

  const uploadRules = async () => {
    toast.dismiss();
    if (fileName.trim() === "") {
      toast.error("Please enter a file name");
      return;
    }
    if (!selectedProject) {
      toast.error("Please select a project");
      return;
    }
    if (currentMaps.length === 0) {
      toast.error("No rules to upload");
      return;
    }

    if (editStateData.editMode && !saveAsNew) {
      try {
        setIsLoading(true);
        const response = await axiosAPI(
          "put",
          `mappings/${editFileData.fileID}`,
          {
            fileName: fileName.trim(),
            projectID: selectedProject,
            rules: currentMaps,
            inputColumns: inputColsData,
            outputColumns: outputColsData,
            fileSettings,
            outputFileSettings,
            description,
          },
        );

        if (response.success) {
          toast.success("Rules updated successfully");
        }
      } catch (error) {
        toast.error("Error updating rules");
      } finally {
        setIsLoading(false);
      }
    } else {
      try {
        setIsLoading(true);
        const response = await axiosAPI("post", "mappings", {
          fileID: saveAsNew ? null : editFileData?.fileID || null,
          fileName: fileName.trim() + (saveAsNew ? " (Copy)" : ""),
          projectID: selectedProject,
          rules: currentMaps,
          inputColumns: inputColsData,
          outputColumns: outputColsData,
          fileSettings,
          outputFileSettings,
          description,
        });

        if (response.success) {
          dispatch(
            setProjectData({
              selectedProjectID: selectedProject,
              fileName: fileName.trim(),
              fileID: response.data._id,
            }),
          );
          toast.success("Rules uploaded successfully");
        }
      } catch (error) {
        toast.error(error.message);
      } finally {
        setIsLoading(false);
      }
    }
  };

  if (isLoading === "fetchingProjects") {
    return (
      <div className="flex flex-col items-center justify-center w-full h-full">
        <div className="text-center">
          <div className="inline-flex items-center px-4 py-2 bg-white rounded-lg shadow-md">
            {miniLoadingCricle}
            <span className="text-gray-700 font-medium">
              Loading Please wait...
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center w-full h-full">
      <div className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md mx-auto">
          <div className="bg-white shadow-xl rounded-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-8">
              <h2 className="text-2xl font-bold text-center text-white mb-2">
                {editStateData?.editMode ? "Update Mappings" : "Save Mappings"}
              </h2>
              <p className="text-center text-blue-100 text-sm">
                {editStateData?.editMode
                  ? "Modify your existing mapping rules"
                  : "Create new mapping rules for your project"}
              </p>
            </div>

            {/* Form Content */}
            <div className="px-6 py-8">
              <div className="space-y-6">
                {/* Project Selection */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Select Project <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      value={selectedProject || editFileData?.projectID || ""}
                      onChange={(e) => setSelectedProject(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 shadow-sm appearance-none"
                      required
                    >
                      <option value="">Choose a project...</option>
                      {projects.map((project) => (
                        <option key={project._id} value={project._id}>
                          {project.projectName}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                      <svg
                        className="w-5 h-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>
                  {projects.length === 0 && (
                    <p className="mt-2 text-sm text-amber-600 bg-amber-50 px-3 py-2 rounded-md">
                      ⚠️ No projects available. Please create a project first.
                    </p>
                  )}
                </div>

                {/* File Name Input */}
                <div>
                  <InputField
                    label="Mapping Name"
                    type="text"
                    placeholder="Enter mapping name (e.g., Customer Data Import)"
                    value={fileName}
                    onChange={(e) => setFileName(e.target.value)}
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Give your mapping rule a descriptive name for easy
                    identification
                  </p>
                </div>

                {/* Rules Summary */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">
                    Mappings Summary
                  </h4>
                  <div className="text-sm text-gray-600">
                    <p>
                      📋 <strong>{currentMaps.length}</strong> mapping rules
                      defined
                    </p>
                    <p>
                      📥 <strong>{inputColsData.length}</strong> input columns
                    </p>
                    <p>
                      📤 <strong>{outputColsData.length}</strong> output columns
                    </p>
                  </div>
                </div>
                {editStateData?.editMode && (
                  <div className="min-h-10 flex ">
                    <CheckBox
                      name={"saveAsNew"}
                      chkBoxText={`Save As New`}
                      checked={saveAsNew}
                      onChange={(e) => setsaveAsNew(e.target.checked)}
                    />
                  </div>
                )}

                {/* Submit Button */}
                <button
                  onClick={uploadRules}
                  className={`w-full px-6 py-4 text-lg font-semibold rounded-lg transition-all duration-200 ${
                    isLoading
                      ? "bg-gray-400 cursor-not-allowed text-gray-200"
                      : "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  }`}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <svg
                        className="w-5 h-5 mr-3 text-gray-200 animate-spin"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v8H4z"
                        ></path>
                      </svg>
                      {editStateData?.editMode ? "Updating..." : "Saving..."}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      {editStateData?.editMode ? "Update" : "Save"}
                    </div>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Helper Text */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              {editStateData?.editMode
                ? "Your changes will be applied to the existing mapping configuration"
                : "Once saved, these rules can be used to process your data files"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
export default SaveRules;
